/**
 * Checklist entity: the backend-computed compliance checklist for a company.
 *
 * Unlike documents/assertions (stored instances), the checklist is COMPUTED on
 * the backend at request time and changes with the company's own data (its
 * headcount, its type, ...). So it is fetched fresh from
 * GET /api/companies/{companyId}/checklist rather than derived from stored
 * document-instance categories on the client.
 *
 * The wire enums are camelCase (category/status/scope); the CATEGORY_TO_KEBAB
 * map below bridges the category enum to the kebab DocCategory the app's
 * CATEGORY_META is keyed on, so the page can reuse the existing section labels.
 */

import type { DocCategory } from '../../app/types';
import { apiGet, apiPost } from '../apiClient';

/** Backend document/attestation category enum (camelCase on the wire). */
export type ChecklistCategory =
  | 'legallyRequired'
  | 'essential'
  | 'bestPractice'
  | 'optional'
  | 'notRelevant'
  | 'shouldNotHave';

/**
 * Resolved status of a checklist item. `needsAnswer` means an asked predicate this item depends on is
 * unanswered, so its tier is unknown (category is null) until the entity answers its questions — never
 * guessed as not-relevant. See COMPLIANCE-MODEL.md §6.
 */
export type ChecklistStatus = 'missing' | 'present' | 'inert' | 'shouldNotHave' | 'needsAnswer';

/**
 * What reading a held document found. Orthogonal to `status`: status says whether the slot is FILLED,
 * health says whether what fills it is any GOOD.
 *
 * `unchecked` is not a pass. It means nothing has read what is on file now — either no assessment
 * exists, or the one that does was written against a file version since replaced. It must never be
 * shown as clean: "we have not read this" and "we read this and it was fine" are different claims
 * (COMPLIANCE-MODEL.md §6).
 */
export type DocumentHealth = 'notApplicable' | 'unchecked' | 'clean' | 'hasFindings';

/**
 * Which half of the requirements page an item belongs on, split by ENFORCEMENT ROUTE:
 * `employment` means an employee, worker or candidate could rely on it (or its absence) in an
 * employment tribunal; `business` means a regulator enforces it (ICO, HSE, HMRC, Home Office,
 * Companies House, FCA) and no tribunal claim can be founded on its absence.
 *
 * Presentation only. The backend computes it from the catalog type id alone, so it can never affect
 * `category` or `status` (see Reeve.Core/Compliance/RequirementSections.cs).
 */
export type RequirementArea = 'employment' | 'itData' | 'business';

/** The working group within an area. Mirrors the backend RequirementSection enum. */
export type RequirementSection =
  | 'recruitment'
  | 'contracts'
  | 'pay'
  | 'timeOff'
  | 'sickness'
  | 'familyLeave'
  | 'conduct'
  | 'leaving'
  | 'fairness'
  | 'wellbeing'
  | 'peopleRecords'
  | 'safety'
  | 'financialCrime'
  | 'payroll'
  | 'screening'
  | 'companyRecords'
  | 'data'
  | 'dataRequests'
  | 'devices';

/** Who/what the item applies to. */
export type ChecklistScope =
  | 'employerLevel'
  | 'perEmployee'
  | 'perWorker'
  | 'perCandidate'
  | 'perRole'
  | 'perCase';

/**
 * A resolved catalog entry: a document type or attestation type, with its
 * computed relevance (needed/has) and status for this company. `id` is the
 * catalog slug (the DocumentType/AssertionType CatalogId), which the upload
 * flow passes back as `catalogId` to satisfy the slot.
 */
export interface ChecklistItem {
  id: string;
  name: string;
  family: string;
  /** Tribunal-facing or regulator-facing: which tab of the requirements page this shows on. */
  area: RequirementArea;
  /** The working group within that tab. */
  section: RequirementSection;
  scope: ChecklistScope;
  /** Null when status is `needsAnswer`: the tier is unknown until the entity answers its questions. */
  category: ChecklistCategory | null;
  needed: boolean;
  has: boolean;
  status: ChecklistStatus;
  /** For a held document, what reading it found. `notApplicable` when nothing is on file. */
  health: DocumentHealth;
  /** Worst finding outstanding, or null when there are none (or none were looked for). */
  topSeverity: ChecklistFindingSeverity | null;
  /** How many findings are outstanding on the held instance. */
  findingCount: number;
  /** Held file's original name, so the list can offer Download without fetching the detail first. */
  fileName?: string | null;
  /** Held file's content type; paired with `fileName` for the export picker. */
  contentType?: string | null;
  /**
   * What the obligation IS, in practice. For an attestation this is the whole explanation available,
   * since there is no document to open and read.
   */
  why: string;
  /**
   * For an attestation, why it applies to THIS company ("You employ at least one worker"). A different
   * question from `why`, and keeping them apart is the point: the applicability condition was
   * previously all the user got, which explained nothing about what they were being asked to certify.
   */
  appliesWhen?: string;
  /**
   * For a PRESENT document, the id of the held instance that satisfies the slot —
   * link the card to /app/documents/{documentId}. Absent for missing/inert/
   * should-not-have documents and for every attestation.
   */
  documentId?: string;
  /**
   * Whether a fill-in template exists for this type, so a MISSING document can offer "Use a template"
   * alongside Upload. Always false for attestations.
   */
  templateAvailable: boolean;
}

/**
 * The weighted compliance score. Headline this rather than present/needed: a raw count weights a
 * missing sabbatical policy the same as a missing written statement of particulars, so it falls
 * hardest exactly where the stakes are lowest.
 *
 * Points per obligation: legally required 1, essential 0.5, best practice 0.25, optional 0 (on both
 * sides, so 100% is reachable without doing optional work). Findings halve. A held should-not-have
 * deducts a full point. See Reeve.Core/Compliance/ComplianceScoring.cs.
 */
export interface ComplianceScore {
  earned: number;
  possible: number;
  /** 0-100. */
  percent: number;
  /** Held documents nothing has read yet: the score counts them, so it is provisional by this much. */
  unchecked: number;
  withFindings: number;
  /** Documents held that shouldn't be. */
  problems: number;
  penalty: number;
}

/** One tier's progress, for the header counters. */
export interface TierProgress {
  tier: ChecklistCategory;
  needed: number;
  present: number;
  withFindings: number;
}

export interface ChecklistSummary {
  needed: number;
  present: number;
  missing: number;
  inert: number;
  /** Held documents the company shouldn't have. Only counts the ones actually on file. */
  shouldNotHave: number;
  /** Items whose tier can't be resolved because an asked predicate they depend on is unanswered. */
  needsAnswer: number;
  score: ComplianceScore;
  tiers: TierProgress[];
}

/**
 * Severity of an assessment finding. Findings are always issues to fix (there is no
 * "ok" finding — a passed check simply produces no finding); the tier drives colour
 * and ordering: critical (red) → important (amber) → suggestion (green).
 */
export type ChecklistFindingSeverity = 'critical' | 'important' | 'suggestion';

/** Where a finding sits in the document (mirrors the backend FindingAnchor). */
export interface ChecklistFindingAnchor {
  paraId: string;
  quote?: string | null;
  occurrence: number;
}

/** An automatic fix operation on the finding's anchored span (mirrors the backend FixOperation). */
export interface ChecklistFixOperation {
  kind: 'replace' | 'insert' | 'remove';
  text?: string | null;
  where?: 'before' | 'after' | null;
}

export interface ChecklistFinding {
  id: string;
  severity: ChecklistFindingSeverity;
  /** Short headline. */
  title: string;
  /** The issue in full. */
  text: string;
  /** Human instruction for fixing it. */
  action: string;
  basis?: { act: string; section?: string | null; url?: string | null } | null;
  anchor?: ChecklistFindingAnchor | null;
  /** The automatic operation, when auto-applicable; null → needs a human. */
  fix?: ChecklistFixOperation | null;
  fixLabel?: string | null;
}

/** The held file behind a present document: enough to show it and mint a download. */
export interface ChecklistDocumentInstance {
  /** Document instance id — pass to getDocumentFileTicket to download the file. */
  documentId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  version: number;
  uploadedAt: string;
}

/**
 * One document type's live detail for a company, resolved on the backend at request
 * time (category/status computed from the company's data, never read from a stored
 * instance category). `instance` is null for a missing document; `findings` is the
 * held instance's latest assessment (empty until an assessment runs), which drives the
 * "in place, things to fix" state.
 */
export interface ChecklistDocumentDetail {
  id: string;
  name: string;
  family: string;
  /** Which requirements tab this belongs on, so "All documents" returns to the right one. */
  area: RequirementArea;
  scope: ChecklistScope;
  /** Null when status is `needsAnswer`. */
  category: ChecklistCategory | null;
  status: ChecklistStatus;
  /**
   * For a held document, what reading it found. Needed because an empty `findings` list is ambiguous
   * on its own: it means "checked, nothing wrong" and "nothing has read this yet" equally.
   */
  health: DocumentHealth;
  needed: boolean;
  /** Whether a fill-in template exists, so a missing document can offer "Use a template". */
  templateAvailable: boolean;
  why: string;
  checks: string[];
  findings: ChecklistFinding[];
  instance: ChecklistDocumentInstance | null;
}

export interface ChecklistDto {
  companyId: string;
  /** Resolved document types, missing-first then by severity. */
  documents: ChecklistItem[];
  /** Resolved attestation types (name = the statement). */
  attestations: ChecklistItem[];
  summary: ChecklistSummary;
}

/** GET /api/companies/{companyId}/checklist — the backend-computed checklist. */
export function getChecklist(companyId: string): Promise<ChecklistDto> {
  return apiGet<ChecklistDto>(`/api/companies/${encodeURIComponent(companyId)}/checklist`);
}

/** One entity's resolved checklist. Same row shape as the company list. */
export interface EntityChecklistDto {
  entityId: string;
  documents: ChecklistItem[];
  attestations: ChecklistItem[];
  summary: ChecklistSummary;
}

/**
 * GET {base}/{id}/checklist — the obligations scoped to ONE employee or case, resolved against that
 * entity's own captured answers. The company list carries only employer-level types; scope routes each
 * type to exactly one of the two.
 */
export function getEntityChecklist(base: string, id: string): Promise<EntityChecklistDto> {
  return apiGet<EntityChecklistDto>(`${base}/${encodeURIComponent(id)}/checklist`);
}

/**
 * GET /api/companies/{companyId}/checklist/documents/{slug} — one document type's live
 * detail (category/status/why/checks + held instance), resolved at request time.
 */
export function getChecklistDocument(
  companyId: string,
  slug: string,
): Promise<ChecklistDocumentDetail> {
  return apiGet<ChecklistDocumentDetail>(
    `/api/companies/${encodeURIComponent(companyId)}/checklist/documents/${encodeURIComponent(slug)}`,
  );
}

/**
 * Create-or-confirm (or clear) a checklist attestation BY TYPE. A checklist attestation is a
 * catalog type that may have no stored state yet, so the backend creates the state on first
 * confirm. Returns the freshly resolved item, so its status already reflects the change
 * (present after confirm, missing after clear).
 */
export function confirmChecklistAttestation(
  companyId: string,
  typeId: string,
  confirmed: boolean,
): Promise<ChecklistItem> {
  return apiPost<ChecklistItem>(
    `/api/companies/${encodeURIComponent(companyId)}/checklist/attestations/${encodeURIComponent(typeId)}/confirm`,
    { confirmed },
  );
}

/**
 * camelCase checklist category -> kebab DocCategory, so the page can reuse
 * CATEGORY_META / CATEGORY_ORDER for section labels, blurbs and ordering.
 */
export const CATEGORY_TO_KEBAB: Record<ChecklistCategory, DocCategory> = {
  legallyRequired: 'legally-required',
  essential: 'essential',
  bestPractice: 'best-practice',
  optional: 'optional',
  notRelevant: 'not-relevant',
  shouldNotHave: 'should-not-have',
};
